/**
 * Permission System
 *
 * Flexible permission-based access control supporting:
 * - Built-in roles: owner, admin, user, viewer
 * - Custom roles in the future
 * - Superadmin (future): backdoor user for support & troubleshooting
 */

/**
 * All available permissions in the system
 * Extensible for custom roles in the future
 */
export enum Permission {
  // Project permissions
  PROJECT_CREATE = 'project:create',
  PROJECT_EDIT = 'project:edit',
  PROJECT_DELETE = 'project:delete',
  PROJECT_VIEW = 'project:view',

  // File permissions
  FILE_CREATE = 'file:create',
  FILE_EDIT = 'file:edit',
  FILE_DELETE = 'file:delete',
  FILE_VIEW = 'file:view',

  // Agent/Chat permissions
  AGENT_CHAT = 'agent:chat',
  AGENT_EXECUTE = 'agent:execute',

  // Build/Deploy permissions
  BUILD_TRIGGER = 'build:trigger',
  DEPLOY_TRIGGER = 'deploy:trigger',
  COMMIT_CREATE = 'commit:create',

  // Settings permissions
  SETTINGS_EDIT = 'settings:edit',
  SECRETS_EDIT = 'secrets:edit',
  CONNECTIONS_EDIT = 'connections:edit',

  // Workspace permissions
  WORKSPACE_CREATE = 'workspace:create',
  WORKSPACE_EDIT = 'workspace:edit',
  MEMBERS_INVITE = 'members:invite',
  MEMBERS_MANAGE = 'members:manage',
}

/**
 * Type derived from enum values
 * Useful for type-safe string literals
 */
export type PermissionType = `${Permission}`;

/**
 * Role-to-permissions mapping
 * Easy to extend with custom roles
 */
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  owner: [
    Permission.PROJECT_CREATE,
    Permission.PROJECT_EDIT,
    Permission.PROJECT_DELETE,
    Permission.PROJECT_VIEW,
    Permission.FILE_CREATE,
    Permission.FILE_EDIT,
    Permission.FILE_DELETE,
    Permission.FILE_VIEW,
    Permission.AGENT_CHAT,
    Permission.AGENT_EXECUTE,
    Permission.BUILD_TRIGGER,
    Permission.DEPLOY_TRIGGER,
    Permission.COMMIT_CREATE,
    Permission.SETTINGS_EDIT,
    Permission.SECRETS_EDIT,
    Permission.CONNECTIONS_EDIT,
    Permission.WORKSPACE_CREATE,
    Permission.WORKSPACE_EDIT,
    Permission.MEMBERS_INVITE,
    Permission.MEMBERS_MANAGE,
  ],
  admin: [
    Permission.PROJECT_CREATE,
    Permission.PROJECT_EDIT,
    Permission.PROJECT_DELETE,
    Permission.PROJECT_VIEW,
    Permission.FILE_CREATE,
    Permission.FILE_EDIT,
    Permission.FILE_DELETE,
    Permission.FILE_VIEW,
    Permission.AGENT_CHAT,
    Permission.AGENT_EXECUTE,
    Permission.BUILD_TRIGGER,
    Permission.DEPLOY_TRIGGER,
    Permission.COMMIT_CREATE,
    Permission.SETTINGS_EDIT,
    Permission.SECRETS_EDIT,
    Permission.CONNECTIONS_EDIT,
    Permission.WORKSPACE_CREATE,
    Permission.WORKSPACE_EDIT,
    Permission.MEMBERS_INVITE,
    Permission.MEMBERS_MANAGE,
  ],
  user: [
    Permission.PROJECT_CREATE,
    Permission.PROJECT_EDIT,
    Permission.PROJECT_VIEW,
    Permission.FILE_CREATE,
    Permission.FILE_EDIT,
    Permission.FILE_VIEW,
    Permission.AGENT_CHAT,
    Permission.AGENT_EXECUTE,
    Permission.BUILD_TRIGGER,
    Permission.DEPLOY_TRIGGER,
    Permission.COMMIT_CREATE,
    Permission.SETTINGS_EDIT,
    Permission.SECRETS_EDIT,
    Permission.CONNECTIONS_EDIT,
    Permission.WORKSPACE_CREATE,
  ],
  viewer: [
    Permission.PROJECT_VIEW,
    Permission.FILE_VIEW,
  ],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: string, permission: Permission): boolean {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(permission);
}

/**
 * Get all permissions for a role
 */
export function getPermissions(role: string): Permission[] {
  return ROLE_PERMISSIONS[role] || [];
}

// =============================================================================
// Workspace Permissions (shared between frontend & backend)
// =============================================================================

/**
 * Workspace permissions object
 * Used by both frontend (usePermissions hook) and backend (PermissionsService)
 */
export interface WorkspacePermissions {
  // Role info
  role: string;
  isViewer: boolean;
  isUser: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  isAdminOrOwner: boolean;

  // Permission checker
  can: (permission: Permission) => boolean;
  permissions: Permission[];

  // Convenience methods for common checks
  canCreateProject: boolean;
  canEditProject: boolean;
  canDeleteProject: boolean;
  canEditFiles: boolean;
  canUseAgent: boolean;
  canBuild: boolean;
  canDeploy: boolean;
  canCommit: boolean;
  canEditSettings: boolean;
  canEditSecrets: boolean;
  canCreateWorkspace: boolean;
  canManageWorkspace: boolean;
  canInviteMembers: boolean;
}

/**
 * Input for creating workspace permissions
 */
export interface WorkspacePermissionsInput {
  /** The stored role in workspace_members table ('admin' | 'user' | 'viewer') */
  role?: string;
  /** Whether user is the workspace owner (computed from workspace.ownerId) */
  isOwner?: boolean;
}

/**
 * Create workspace permissions object from role and ownership
 *
 * Usage:
 * - Frontend: createWorkspacePermissions({ role: workspace.role, isOwner: workspace.isOwner })
 * - Backend: createWorkspacePermissions({ role: membership.role, isOwner: workspace.ownerId === userId })
 */
export function createWorkspacePermissions(
  input: WorkspacePermissionsInput,
): WorkspacePermissions {
  const { role: actualRole = 'viewer', isOwner = false } = input;

  // Determine effective role (owner takes precedence for permissions)
  const effectiveRole = isOwner ? 'owner' : actualRole;

  // Get permissions for this role
  const permissions = ROLE_PERMISSIONS[effectiveRole] || [];

  // Role flags (actualRole is what's stored in DB, not 'owner')
  const isAdmin = actualRole === 'admin';
  const isAdminOrOwner = isAdmin || isOwner;

  return {
    // Role info - use effective role for display
    role: effectiveRole,
    isViewer: effectiveRole === 'viewer',
    isUser: effectiveRole === 'user',
    isAdmin,
    isOwner,
    isAdminOrOwner,

    // Permission checker
    can: (permission: Permission) => hasPermission(effectiveRole, permission),
    permissions,

    // Convenience methods
    canCreateProject: hasPermission(effectiveRole, Permission.PROJECT_CREATE),
    canEditProject: hasPermission(effectiveRole, Permission.PROJECT_EDIT),
    canDeleteProject: hasPermission(effectiveRole, Permission.PROJECT_DELETE),
    canEditFiles: hasPermission(effectiveRole, Permission.FILE_EDIT),
    canUseAgent: hasPermission(effectiveRole, Permission.AGENT_CHAT),
    canBuild: hasPermission(effectiveRole, Permission.BUILD_TRIGGER),
    canDeploy: hasPermission(effectiveRole, Permission.DEPLOY_TRIGGER),
    canCommit: hasPermission(effectiveRole, Permission.COMMIT_CREATE),
    canEditSettings: hasPermission(effectiveRole, Permission.SETTINGS_EDIT),
    canEditSecrets: hasPermission(effectiveRole, Permission.SECRETS_EDIT),
    canCreateWorkspace: hasPermission(effectiveRole, Permission.WORKSPACE_CREATE),
    canManageWorkspace: hasPermission(effectiveRole, Permission.MEMBERS_MANAGE),
    canInviteMembers: hasPermission(effectiveRole, Permission.MEMBERS_INVITE),
  };
}
